import type { ShortVideoProperty } from '~/core/entities/property';
import type { ComputedRef } from 'vue';

import { floatToLocaleString } from '#imports';
import { grammarInflection } from '~~/utils';

export function usePropertyInfo(
	property: ComputedRef<
		ShortVideoProperty | Readonly<ShortVideoProperty> | undefined
	>
) {
	const formattedPrice = computed(() => {
		const price = property.value?.askingPrice;
		if (!price) return null;
		return floatToLocaleString(price, { maximumFractionDigits: 0 });
	});

	const formattedArea = computed(() => {
		if (!property.value?.area) return null;
		return new Intl.NumberFormat('pt-BR', {
			maximumFractionDigits: 0
		}).format(Math.round(property.value.area));
	});

	const roomsInfo = computed(() => {
		if (!property.value) return null;
		const { suites, bedrooms } = property.value;
		if (suites && suites > 0)
			return {
				count: suites,
				label: grammarInflection(suites, 'suíte', 'suítes')
			};
		if (bedrooms && bedrooms > 0)
			return {
				count: bedrooms,
				label: grammarInflection(bedrooms, 'quarto', 'quartos')
			};
		return null;
	});

	const propertyTypeName = computed(
		() => property.value?.propertyType?.name ?? ''
	);

	const region = computed(() => property.value?.region?.trim() ?? '');

	const propertyLocation = computed(() => {
		const typeName = propertyTypeName.value;
		const r = region.value;
		if (typeName && r) return `${typeName} em ${r}`;
		return r || typeName || '';
	});

	const cityState = computed(() => {
		if (!property.value) return '';
		const { city, state } = property.value;
		if (city && state) return `${city} - ${state}`;
		return city ?? state ?? '';
	});

	return {
		formattedPrice,
		formattedArea,
		roomsInfo,
		propertyTypeName,
		region,
		propertyLocation,
		cityState
	};
}
